"use client";

import { motion, useAnimation } from "motion/react";
import type { HTMLAttributes } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

import { cn } from "@/lib/utils";

export interface EyeIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface EyeIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
}

/**
 * 「预览」导航项的图标（2026-10-07）。
 *
 * 动画是**眨眼**：整个眼睛沿 Y 轴压扁到一条线再弹回 —— 比「瞳孔左右看」
 * 更像「看一眼」，也更符合「预览」的语义。
 *
 * ⚠️ SVG 的 `transform-origin: center` 默认按**视口**算，不是按图形本身算，
 * 所以要显式 `transformBox: "fill-box"`，否则眼睛会绕 SVG 左上角压扁。
 *
 * 路径照搬 Lucide 的 `eye`（与仓库里其它 30 个图标同源 —— 比如 `scan-text.tsx`
 * 就是 Lucide 的路径）。手写的杏仁形眼角太尖，视觉上与其余导航图标不一致。
 */
const BLINK_DOWN = {
  scaleY: 0.12,
  transition: { duration: 0.12, ease: "easeIn" as const },
};

const BLINK_UP = {
  scaleY: 1,
  transition: { duration: 0.18, ease: "easeOut" as const },
};

const EyeIcon = forwardRef<EyeIconHandle, EyeIconProps>(
  ({ onMouseEnter, onMouseLeave, className, size = 28, ...props }, ref) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    const blink = useCallback(async () => {
      await controls.start(BLINK_DOWN);
      await controls.start(BLINK_UP);
    }, [controls]);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: blink,
        stopAnimation: () => controls.start({ scaleY: 1 }),
      };
    });

    const handleMouseEnter = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseEnter?.(e);
        } else {
          await blink();
        }
      },
      [blink, onMouseEnter]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseLeave?.(e);
        } else {
          // 回到未压扁的状态（与 stopAnimation 同一个目标值）
          controls.start({ scaleY: 1 });
        }
      },
      [controls, onMouseLeave]
    );

    return (
      <div
        className={cn(className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.g
            animate={controls}
            style={{ transformBox: "fill-box", transformOrigin: "center" }}
          >
            {/* 眼型（Lucide eye） */}
            <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
            {/* 瞳孔 */}
            <circle cx="12" cy="12" r="3" />
          </motion.g>
        </svg>
      </div>
    );
  }
);

EyeIcon.displayName = "EyeIcon";

export { EyeIcon };
